/**
 * Getypeerde publish/subscribe tussen systemen.
 *
 * Systemen praten via de bus in plaats van via directe referenties, zodat de tafellaag
 * later ingeplugd kan worden zonder de straatlaag te herschrijven (zie docs/ARCHITECTURE.md).
 */

/** Alle events in het spel, met hun payload. Voeg hier toe, niet ad hoc elders. */
export interface GameEvents {
  'player:damaged': { amount: number; healthLeft: number };
  'player:died': Record<string, never>;
  'player:step': { x: number; z: number; speed: number };
  'combat:shot': { x: number; z: number; weapon: string; faction: 'vitale' | 'barili' | 'neutral' };
  'combat:impact': { x: number; z: number; outcome: 'target' | 'wall' };
  'weapon:reload': { x: number; z: number; weapon: string; faction: 'vitale' | 'barili' | 'neutral' };
  'enemy:killed': { id: number };
  'enemy:alerted': { id: number; position: { x: number; z: number }; radius: number };
  'soldier:rescued': { id: number; remaining: number };
  'soldier:down': { id: number; remaining: number };
  'mission:objectiveChanged': { text: string };
  'mission:completed': { id: string };
  'mission:failed': { id: string; reason: string };
  'safehouse:entered': { id: string; name: string };
  'vehicle:entered': { id: string; kind: 'sedan' | 'van' | 'wreck' };
  'vehicle:exited': { id: string; kind: 'sedan' | 'van' | 'wreck' };
  'vehicle:damaged': { id: string; kind: 'sedan' | 'van' | 'wreck'; amount: number; reason: string; speed: number; x: number; z: number; healthLeft: number };
  'vehicle:burned': { id: string; kind: 'sedan' | 'van' | 'wreck' };
  'vehicle:ranOver': { id: string; x: number; z: number; speed: number; faction: 'vitale' | 'barili' | 'neutral' };
  'vehicle:stolen': { id: string; kind: 'sedan' | 'van' | 'wreck' };
  /** Banden die grip verliezen: hard sturen op snelheid. `severity` loopt van 0 tot 1. */
  'vehicle:skid': { id: string; x: number; z: number; severity: number };
  'chase:started': { level: 'searching' | 'active' | 'escaping'; reason: 'alert' | 'shot' };
  'chase:escaped': { seconds: number };
  'chase:failed': { reason: string };
  'cutscene:started': { id: string };
  'cutscene:beat': { id: string; index: number; text?: { speaker?: string; line: string } };
  'cutscene:ended': { id: string };
  'cutscene:skipped': { id: string };
  'dialogue:started': { id: string; nodeId: string };
  'dialogue:nodeChanged': { id: string; nodeId: string };
  'dialogue:choiceSelected': { id: string; nodeId: string; choiceIndex: number; label: string };
  'dialogue:ended': { id: string };
  'reputation:changed': { track: 'police' | 'populace' | 'rivals'; value: number };
}

export type EventName = keyof GameEvents;
type Handler<K extends EventName> = (payload: GameEvents[K]) => void;

export class EventBus {
  readonly #handlers = new Map<EventName, Set<Handler<EventName>>>();

  on<K extends EventName>(event: K, handler: Handler<K>): () => void {
    let set = this.#handlers.get(event);
    if (!set) {
      set = new Set();
      this.#handlers.set(event, set);
    }
    set.add(handler as Handler<EventName>);
    return () => this.off(event, handler);
  }

  once<K extends EventName>(event: K, handler: Handler<K>): () => void {
    const off = this.on(event, (payload) => {
      off();
      handler(payload);
    });
    return off;
  }

  off<K extends EventName>(event: K, handler: Handler<K>): void {
    this.#handlers.get(event)?.delete(handler as Handler<EventName>);
  }

  emit<K extends EventName>(event: K, payload: GameEvents[K]): void {
    const set = this.#handlers.get(event);
    if (!set) return;
    // Kopie: een handler mag zich tijdens de emit afmelden zonder de iteratie te breken.
    for (const handler of [...set]) {
      (handler as Handler<K>)(payload);
    }
  }

  clear(): void {
    this.#handlers.clear();
  }
}

/** Eén bus voor het hele spel. */
export const bus = new EventBus();
